import React, { useEffect } from 'react';
import styled from 'styled-components';
import {
  cssColorValue,
  semanticColors,
  spacer16,
  spacer24,
  spacer32,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';

import Metatags from 'components/Metatags/Metatags';
import PageContainer from 'components/PageContainer';
import Breadcrumbs from 'components/Breadcrumbs';
import BlockSerializer from 'lib/sanity/ui/BlockSerializer';
import WarningBanner from 'components/WarningBanner/WarningBanner';
import analytics from 'utils/client/analytics/analytics';
import { useUbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { BlockSerializerUbiContext } from 'lib/sanity/ui/BlockSerializerUbiProvider';
import { TreatedArticleData, TreatmentState } from 'utils/remote-config/content/types';

import RelatedArticles from './RelatedArticles';
import FeedbackWidget from './FeedbackWidget';

export interface ArticleProps {
  article: TreatedArticleData;
  articleSlug: string;
  isPlain: boolean;
}

const Title = styled(Type)`
  word-break: break-word;
  hyphens: auto;
`;

const Card = styled.div`
  margin-top: ${spacer32};
  padding: ${spacer24} ${spacer16};
  border-radius: 8px;
  background: ${cssColorValue(semanticColors.backgroundTintedBase)};
`;

const BottomContainer = styled.div`
  margin-block-start: ${spacer24};
  padding: ${spacer8};
`;

function getTreatmentIdentifier(article: TreatedArticleData, articleSlug: string): string {
  switch (article.treatmentState) {
    case TreatmentState.Control:
      return `${articleSlug}_control`;
    case TreatmentState.Treatment:
      return `${articleSlug}_treatment_${article.treatmentKey}`;
    default:
      return articleSlug;
  }
}

const Article = ({ article, isPlain, articleSlug }: ArticleProps) => {
  const articleIdentifier = getTreatmentIdentifier(article, articleSlug);
  const ubi = useUbiArticlePage(articleIdentifier);

  useEffect(() => {
    analytics.trackPageView(`/article/${articleIdentifier}/`);
    ubi.logArticleImpression();
  }, [articleIdentifier, ubi]);

  const { content, title, relatedArticles, category, banner } = article;

  const parentCategory = category?.parentCategory;

  return (
    <>
      <Metatags title={title as string} urlPath={`article/${articleSlug}/`} />
      {!isPlain && <WarningBanner banner={banner} />}
      <PageContainer>
        {!isPlain && parentCategory && <Breadcrumbs parentCategory={parentCategory} article={article} ubi={ubi} />}

        {content && (
          <Card>
            <Title forwardedAs="h1" variant="altoBrio" paddingBottom={spacer16}>
              {title}
            </Title>
            <BlockSerializerUbiContext.Provider value={ubi}>
              <BlockSerializer value={content} />
            </BlockSerializerUbiContext.Provider>
          </Card>
        )}

        {!isPlain && relatedArticles.length > 0 && (
          <Card>
            <RelatedArticles relatedArticles={relatedArticles} ubi={ubi} />
          </Card>
        )}

        <BottomContainer>{!isPlain && <FeedbackWidget articleSlug={articleSlug} ubi={ubi} />}</BottomContainer>
      </PageContainer>
    </>
  );
};

export default Article;
